import { Icon } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Autocomplete, Popover, Stack, TextField } from '@mui/material';
import { memo, useRef, useState } from 'react';
import { InputTags } from './components/input-tags';
import { OptionsTabs } from './components/options-tabs';
import * as Styled from './styles';
import * as SX from './sx';
import type { MultipleSelectProps as Props } from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'left' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'left' as const,
};

interface Option {
  id: number;
  title: string;
  count: number;
}

export const MultipleSelect = memo(
  ({ className, inputIcon, iconSx }: Props) => {
    const options: Option[] = [
      { id: 1, title: 'Mehrantest2023', count: 2000 },
      { id: 2, title: 'Testkain', count: 2000 },
    ];

    const [anchorEl] = useState<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLDivElement | null>(null);

    const popoverState = useBoolean();

    const handleOpenPopover = () => {
      popoverState.onTrue();
    };

    const handleClosePopover = () => {
      popoverState.onFalse();
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
      <Styled.Wrapper
        className={classnames('MultipleSelect', className)}
        sx={SX.root}
      >
        {inputIcon && (
          <Stack
            alignContent="center"
            justifyContent="center"
            component="span"
            sx={SX.iconWrapper}
          >
            <Icon name={inputIcon} size={22} sxx={iconSx} />
          </Stack>
        )}
        <Autocomplete
          multiple
          sx={SX.autoComplete}
          options={options}
          getOptionLabel={(option) => option.title}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Option"
              autoComplete="off"
              variant="outlined"
              onClick={handleOpenPopover}
              ref={inputRef}
            />
          )}
          onChange={() => {}}
          value={[]}
          renderTags={(value: readonly Option[], getTagProps) =>
            value.map((option: Option, index: number) => (
              <InputTags
                label={option.title}
                key={option.id}
                index={index}
                action={getTagProps}
              />
            ))
          }
        />
        <Popover
          sx={SX.popover}
          id={id}
          open={popoverState.value}
          anchorEl={inputRef.current}
          onClose={handleClosePopover}
          anchorOrigin={ANCHOR_ORIGIN}
          transformOrigin={TRANSFORM_ORIGIN}
        >
          <OptionsTabs />
        </Popover>
      </Styled.Wrapper>
    );
  }
);

MultipleSelect.displayName = 'MultipleSelect';
